import { ReactNode, useState } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { StickyActionBar } from "@/components/ui/sticky-action-bar";

export interface ProposalStep {
  id: string;
  label: string;
  icon: ReactNode;
  content: ReactNode;
  /** If true, step is considered complete */
  completed?: boolean;
  /** If true, step is hidden (e.g. locked features) */
  hidden?: boolean;
}

interface ProposalBuilderFlowProps {
  steps: ProposalStep[];
  /** Optional: control starting step */
  initialStep?: number;
}

const ProposalBuilderFlow = ({ steps, initialStep = 0 }: ProposalBuilderFlowProps) => {
  const visibleSteps = steps.filter((s) => !s.hidden);
  const [currentStep, setCurrentStep] = useState(initialStep);

  const goNext = () => setCurrentStep((s) => Math.min(s + 1, visibleSteps.length - 1));
  const goBack = () => setCurrentStep((s) => Math.max(s - 1, 0));
  const goToStep = (i: number) => setCurrentStep(i);

  const activeStep = visibleSteps[currentStep];

  return (
    <div className="w-full max-w-4xl mx-auto space-y-2">
      {/* Step indicator */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-border pb-4 pt-2 px-1 -mx-1">
        <div className="flex items-center justify-between px-1">
          {visibleSteps.map((step, i) => (
            <button
              key={step.id}
              type="button"
              onClick={() => goToStep(i)}
              className="flex flex-col items-center gap-1 flex-1 cursor-pointer group"
            >
              <div
                className={cn(
                  "h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 border-2",
                  i === currentStep
                    ? "bg-primary border-primary text-primary-foreground ring-2 ring-primary/30"
                    : step.completed
                      ? "bg-primary border-primary text-primary-foreground"
                      : "bg-secondary border-border text-muted-foreground group-hover:border-primary/50"
                )}
              >
                {step.completed && i !== currentStep ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span
                className={cn(
                  "text-[10px] text-center leading-tight max-w-[80px] hidden sm:block",
                  i === currentStep
                    ? "text-primary font-semibold"
                    : step.completed
                      ? "text-primary font-medium"
                      : "text-muted-foreground"
                )}
              >
                {step.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Active step content */}
      <div className="pt-4 min-h-[400px]">
        {activeStep && (
          <section>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0 bg-primary/15 text-primary">
                {activeStep.icon}
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Step {currentStep + 1} of {visibleSteps.length}
                </p>
                <h2 className="text-lg font-semibold text-foreground leading-tight">
                  {activeStep.label}
                </h2>
              </div>
            </div>
            <div className="w-full">{activeStep.content}</div>
          </section>
        )}
      </div>

      {/* Sticky navigation */}
      <StickyActionBar
        info={
          <span>
            Step <span className="text-foreground font-medium">{currentStep + 1}</span> of {visibleSteps.length}
            {activeStep && <span className="hidden sm:inline"> · {activeStep.label}</span>}
          </span>
        }
      >
        <Button
          variant="outline"
          onClick={goBack}
          disabled={currentStep === 0}
          className="flex items-center gap-2"
          size="sm"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={goNext}
          disabled={currentStep === visibleSteps.length - 1}
          className="flex items-center gap-2"
          size="sm"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </StickyActionBar>
    </div>
  );
};

export default ProposalBuilderFlow;
